// Push Devices Manager component for managing browser push subscriptions
import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Switch, Spin } from 'antd';
import {
  BellOutlined,
  DeleteOutlined,
  MobileOutlined,
  GlobalOutlined,
  CloseCircleFilled,
  ExclamationCircleFilled,
  CloseOutlined,
  DownOutlined,
} from '@ant-design/icons';
import toast from 'react-hot-toast';
import {
  registerBrowserPush,
  unsubscribeBrowserPush,
  removePushSubscription,
  getUserPushSubscriptions,
  getCurrentFCMToken,
  getSubscriptionId,
  PushSubscriptionItem,
} from 'src/services/push/registerPush';
import { sessionStore } from 'src/store/sessionStore';
import { auth } from 'src/config/firebase';
import './PushDevicesManager.scss';

interface PushDevicesManagerProps {
  open: boolean;
  onClose: () => void;
}

export const PushDevicesManager: FC<PushDevicesManagerProps> = ({ open, onClose }) => {
  const currentUser = sessionStore((state) => state.currentUser);
  const trainerUid = auth.currentUser?.uid || currentUser?.uid || '';

  const [enabled, setEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<PushSubscriptionItem[]>([]);
  const [currentSubId, setCurrentSubId] = useState<string | null>(null);
  const [currentFcmToken, setCurrentFcmToken] = useState<string | null>(null);
  const [fetching, setFetching] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const permission =
    typeof window !== 'undefined' && 'Notification' in window
      ? Notification.permission
      : 'unsupported';

  const loadSubscriptions = useCallback(async () => {
    if (!trainerUid) return;
    setFetching(true);
    try {
      const [items, token] = await Promise.all([
        getUserPushSubscriptions(trainerUid, 'trainer'),
        getCurrentFCMToken(),
      ]);
      setSubscriptions(items);
      setCurrentFcmToken(token);

      const activeSubId = token
        ? getSubscriptionId(token)
        : typeof window !== 'undefined'
          ? localStorage.getItem('minipt_current_fcm_sub_id')
          : null;
      setCurrentSubId(activeSubId);

      const isCurrentDeviceRegistered = items.some(
        (sub) => sub.id === activeSubId || (token && sub.fcm_token === token),
      );
      setEnabled(isCurrentDeviceRegistered && permission === 'granted');
    } catch (e) {
      console.warn('Failed to load subscriptions:', e);
    } finally {
      setFetching(false);
    }
  }, [trainerUid, permission]);

  useEffect(() => {
    if (open) {
      loadSubscriptions();
    }
  }, [open, loadSubscriptions]);

  // Sort subscriptions so "This Device" appears at the top if present
  const sortedSubscriptions = useMemo(() => {
    return [...subscriptions].sort((a, b) => {
      const aIsThis =
        (currentSubId && a.id === currentSubId) ||
        (currentFcmToken && a.fcm_token === currentFcmToken);
      const bIsThis =
        (currentSubId && b.id === currentSubId) ||
        (currentFcmToken && b.fcm_token === currentFcmToken);
      if (aIsThis && !bIsThis) return -1;
      if (!aIsThis && bIsThis) return 1;
      return 0;
    });
  }, [subscriptions, currentSubId, currentFcmToken]);

  const handleTogglePush = async (checked: boolean) => {
    if (!trainerUid) return;
    setLoading(true);

    if (checked) {
      const result = await registerBrowserPush(trainerUid, 'trainer');
      if (result.success) {
        toast.success('Push notifications enabled for this browser!');
        setEnabled(true);
        if (result.token) {
          setCurrentFcmToken(result.token);
          setCurrentSubId(result.subId || getSubscriptionId(result.token));
        }
        await loadSubscriptions();
      } else if (result.status === 'DENIED') {
        toast.error('Notification permission was blocked in browser settings.');
        setEnabled(false);
      } else {
        toast.error(result.error || 'Failed to enable notifications.');
        setEnabled(false);
      }
    } else {
      const success = await unsubscribeBrowserPush(trainerUid, 'trainer');
      if (success) {
        toast.success('Push notifications disabled for this browser.');
        setEnabled(false);
        setCurrentFcmToken(null);
        setCurrentSubId(null);
        await loadSubscriptions();
      } else {
        toast.error('Failed to unsubscribe.');
      }
    }
    setLoading(false);
  };

  const handleRemoveDevice = async (sub: PushSubscriptionItem, isCurrentDevice: boolean) => {
    if (!trainerUid) return;
    const success = await removePushSubscription(trainerUid, sub.id, sub.fcm_token, 'trainer');
    if (success) {
      toast.success(
        isCurrentDevice
          ? 'Push notifications disabled for this device.'
          : 'Device subscription removed.',
      );
      if (isCurrentDevice) {
        setEnabled(false);
        setCurrentFcmToken(null);
        setCurrentSubId(null);
      }
      await loadSubscriptions();
    } else {
      toast.error('Failed to remove device.');
    }
  };

  const parseUserAgent = (ua: string): string => {
    if (!ua) return 'Web Browser';
    if (/Chrome/.test(ua) && !/Edg/.test(ua)) return 'Google Chrome';
    if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'Apple Safari';
    if (/Firefox/.test(ua)) return 'Mozilla Firefox';
    if (/Edg/.test(ua)) return 'Microsoft Edge';
    return 'Web Browser';
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      rootClassName="logout-modal push-devices-modal"
      className="logout-modal push-devices-modal"
      width={560}
      styles={{
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(6, 7, 35, 0.8)',
        },
      }}
      closeIcon={
        <div className="modal-close-circle">
          <CloseOutlined />
        </div>
      }
      title={
        <div className="push-modal-header">
          <div className="header-icon-badge">
            <BellOutlined />
          </div>
          <div className="header-text">
            <h2 className="modal-title">Notification Settings</h2>
            <p className="modal-subtitle">
              Manage browser push notifications and registered alert devices.
            </p>
          </div>
        </div>
      }
    >
      <div className="push-manager-content">
        {/* Toggle Master Card */}
        <div className="push-toggle-card">
          <div className="toggle-left">
            <span className="toggle-label">Browser Push Notifications</span>
            <span className="toggle-desc">
              Receive instant desktop & mobile alerts for incoming messages, inquiries, and check-in
              requests.
            </span>
          </div>
          <div className="toggle-right">
            <Switch
              checked={enabled}
              onChange={handleTogglePush}
              loading={loading}
              disabled={loading}
              className="custom-push-switch"
            />
          </div>
        </div>

        {/* Permission Status Pill Row (Only shown if permission is not yet allowed/granted) */}
        {permission !== 'granted' && (
          <div className="permission-row">
            <span className="permission-label">Browser Permission:</span>
            {permission === 'denied' && (
              <div className="permission-pill denied">
                <CloseCircleFilled />
                <span>Blocked in Browser</span>
              </div>
            )}
            {permission === 'default' && (
              <div className="permission-pill default">
                <ExclamationCircleFilled />
                <span>Not Requested Yet</span>
              </div>
            )}
            {permission === 'unsupported' && (
              <div className="permission-pill unsupported">
                <span>Not Supported by Browser</span>
              </div>
            )}
          </div>
        )}

        {/* Registered Devices List */}
        <div className="devices-section">
          {fetching ? (
            <div className="loading-state">
              <Spin size="small" />
              <span>Checking registered devices...</span>
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="empty-devices-card">
              <div className="empty-icon">
                <BellOutlined />
              </div>
              <p className="empty-title">No Active Devices</p>
              <p className="empty-desc">
                Toggle browser notifications on above to register this device for instant alerts.
              </p>
            </div>
          ) : (
            <div className="devices-container">
              {/* Collapsible All Registered Devices */}
              <div className="other-devices-accordion">
                <div className="accordion-trigger" onClick={() => setIsExpanded((prev) => !prev)}>
                  <div className="accordion-left">
                    <span className="section-title">ALL REGISTERED DEVICES</span>
                    <span className="count-badge">{subscriptions.length}</span>
                  </div>
                  <div className="accordion-right">
                    <span className="toggle-text">{isExpanded ? 'Hide' : 'Show'}</span>
                    <DownOutlined className={`arrow-icon ${isExpanded ? 'expanded' : ''}`} />
                  </div>
                </div>

                {isExpanded && (
                  <div className="accordion-content">
                    {sortedSubscriptions.map((sub) => {
                      const isThisDevice = Boolean(
                        (currentSubId && sub.id === currentSubId) ||
                        (currentFcmToken && sub.fcm_token === currentFcmToken),
                      );

                      return (
                        <div
                          key={sub.id}
                          className={`device-card ${isThisDevice ? 'this-device-card' : ''}`}
                        >
                          <div className="device-icon-box">
                            {/Mobile|Android|iPhone/.test(sub.ua) ? (
                              <MobileOutlined />
                            ) : (
                              <GlobalOutlined />
                            )}
                          </div>
                          <div className="device-details">
                            <div className="device-header">
                              <span className="device-name">{parseUserAgent(sub.ua)}</span>
                              {isThisDevice && (
                                <span className="this-device-badge">This Device</span>
                              )}
                              <span className="active-badge">Active</span>
                            </div>
                            <span className="device-time">
                              Registered:{' '}
                              {sub.created_at
                                ? new Date(sub.created_at).toLocaleDateString()
                                : 'Today'}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="delete-device-btn"
                            title={isThisDevice ? 'Remove this device' : 'Remove device'}
                            onClick={() => handleRemoveDevice(sub, isThisDevice)}
                          >
                            <DeleteOutlined />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Actions */}
        <div className="modal-footer-row">
          <button type="button" onClick={onClose} className="done-btn">
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
};
